import React from 'react';
import { Home, ChevronRight, Volume2, Award, ArrowRight, ArrowLeft, Mail, Plane, Medal } from 'lucide-react';
import { ArchivalImageCard } from '../ArchivalImageCard';
import { useVoiceReader } from '../../context/VoiceReaderContext';

interface HarryDentonSectionProps {
  onSelectPart?: (partId: string) => void;
}

export const HarryDentonSection: React.FC<HarryDentonSectionProps> = ({ onSelectPart }) => {
  const { speakCurrentPage, isPlaying } = useVoiceReader();

  return (
    <div className="space-y-10 font-sans text-[#2c221e]">
      {/* Sub-navigation Breadcrumbs & Voice Narrator Controls */}
      <header className="bg-[#f3ede0] p-6 sm:p-8 md:p-10 rounded-3xl border-2 border-[#d8ceb9] shadow-sm space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3 text-xs font-serif text-[#7a5b3a]">
          {/* Breadcrumb: The Dentons > Part 8 of 9: Harry Denton WW2 */}
          <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1.5 sm:gap-2">
            <button
              onClick={() => onSelectPart && onSelectPart('hub')}
              className="hover:underline flex items-center gap-1 font-bold text-[#7a5b3a]"
            >
              <Home className="w-3.5 h-3.5" />
              <span>The Dentons</span>
            </button>
            <ChevronRight className="w-3.5 h-3.5 text-[#d8ceb9]" />
            <span className="bg-[#e8dec9] px-2.5 py-1 rounded-full font-bold uppercase tracking-wider text-[#7a5b3a] text-[11px] sm:text-xs">
              Part 8 of 9: Harry Denton WW2
            </span>
          </nav>

          {/* Audio Narration Trigger */}
          <button
            onClick={speakCurrentPage}
            className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-[#7a5b3a] hover:bg-[#5c4328] text-white text-xs font-bold transition shadow-xs cursor-pointer"
            title="Listen to Harry Denton"
          >
            <Volume2 className={`w-3.5 h-3.5 ${isPlaying ? 'animate-pulse text-amber-200' : 'text-amber-300'}`} />
            <span>{isPlaying ? 'Listening to Section...' : 'Listen to this Chapter'}</span>
          </button>
        </div>

        <h1 className="font-serif text-3xl md:text-5xl font-extrabold text-[#2c221e] tracking-tight leading-tight pt-1">
          FLYING CAPTAIN HARRY DENTON (1920-2006)
        </h1>

        <div className="flex items-center gap-2 text-xs font-serif text-[#7a5b3a] uppercase tracking-wider font-semibold">
          <Plane className="w-3.5 h-3.5" />
          <span>Distinguished Flying Cross • Part 8 of the 9-Part Denton Family History</span>
        </div>
      </header>

      {/* Main Archival Text Content */}
      <article className="bg-[#fbf9f4] p-6 sm:p-10 md:p-12 rounded-3xl border border-[#d8ceb9] shadow-sm space-y-10 text-base md:text-lg leading-relaxed text-[#2c221e]">
        
        {/* Section Header & DFC Image */}
        <section className="space-y-6">
          <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#2c221e]">
            FLYING CAPTAIN HARRY DENTON (1920-2006) Distinguished Flying Cross
          </h2>

          {/* Image 111: Harry Denton DFC */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/111 Harry Denton DFC.jpg"
              alt="Harry Denton DFC"
              caption="Flying Officer Frederick Harry Denton DFC"
              filenameHint="111 Harry Denton DFC.jpg"
            />
          </div>

          <blockquote className="p-6 bg-[#f3ede0] rounded-2xl border-l-4 border-[#7a5b3a] font-serif italic text-lg sm:text-xl text-[#2c221e] shadow-xs">
            "In most perilous circumstances this officer displayed skill, courage and determination of a high order."
          </blockquote>

          <p>
            Frederick Harry Denton (Harry) grew up on Oakdale, Southbridge, the eldest son of Tom and Annie
            Denton. He left school aged 14 to work on the farm - it was something he always regretted as he
            was clearly academic.
          </p>

          <p>
            World War 2 started on 1 September 1939. Harry decided to apply for the air force as he had always
            wanted to fly. He had to study maths and physics to pass the essential exams. He entered the Royal
            New Zealand Air Force at Woodbourne where he was posted to an aerodrome defence unit. He did
            his primary training in Tiger Moths at Ashburton before going to Canada in 1943 for advanced
            training in bombers.
          </p>

          <p>
            The British Commonwealth Air Training Plan took trainees from the UK, Canada, Australia and NZ to
            be trained in 107 flying schools across Canada. More than 130,000 pilots were trained in the wide
            safe spaces of Canada, and which US President Franklin Roosevelt called the "Aerodrome of
            Democracy."
          </p>

          <p>
            Harry graduated top of his class. He was transferred to 9th Squadron at RAF Bardney (Lincolnshire),
            the oldest dedicated bomber squadron of the RAF, and only main force squadron to carry out
            precision bombing. Flying Officer Denton joined a crew as a pilot of a Lancaster bomber.
          </p>

          {/* Image 112: Lancaster bombing raid */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/112 Lancasteer bombing raid.jpg"
              alt="Lancaster bombing raid"
              caption="Lancaster bombing raids and at RAF Bardney (below)"
              filenameHint="112 Lancasteer bombing raid.jpg"
            />
          </div>

          <p>
            On 1 January 1945 Harry was 25, and with a crew of six he was on his fifth bombing raid with 66
            Lancasters to the Dortland-Ems Canal, a strategic industrial highway 269km long and open to large
            vessels. At 10 am and at about 3500m they dropped their bombs but then the plane was hit twice by
            huge anti-aircraft shells, manned by German schoolboys who had been forced to work on the gun
            batteries or be killed.
          </p>

          <p>
            The first shell destroyed the mid-upper turret and set fire to the hydraulic system. Both turrets were
            on fire and quite extensive damage was done to the body of the plane. The second shell blew the
            nose off and the canopy over the pilot which allowed the wind to blow straight through.
          </p>

          <p>
            Harry recalled later that as it was mid-winter the temperatures were freezing and that was one of
            the hardest things he faced in the fight to land the plane. He nearly lost two fingers to frostbite.
            One of the engines was damaged and closed down, the radios and intercom were gone and it was so
            noisy with the wind blowing through that they couldn't talk to the crew.
          </p>

          <p>
            "The plane was very hard to handle because there were two motors on one side and one on the
            other, and having dropped five and half tonnes of bombs, the plane was out of balance." Harry said.
          </p>

          <p>
            "All the flying instruments were gone except the air speed indicator, an altimeter ad a compass but
            the plane was still quite flyable. We did the best we could flying almost due west knowing that within
            an hour we would be over friendly territory.
          </p>

          <p>
            We were losing height at about 34 metres a minute which wasn't worrying me very much because
            we were about 2800 m at this time."
          </p>

          <p>
            The middle part of the plane went on fire, burning the rear gunner who was trying to get out of the
            turret.
          </p>

          <p>
            Harry had been briefly knocked unconscious but came to in time to hear the mid upper gunner say
            they were on fire. It took massive strength to keep the plane flying straight and level.
          </p>

          <p>
            Wireless operator Flight Sergeant George Thompson walked through flames and pulled the severely
            burned and unconscious gunner Ernie Potts to safety past a huge hole in the floor of the plane. Then
            he beat out the flames in the gunner's turret and returned to get Welsh rear gunner Haydn Price
            who had taken his helmet off and whose ears were burnt off as he was pulled to safety.
          </p>

          <p>
            Harry was determined to fly the Lancaster to the Allied part of Holland.
          </p>

          <p>
            "When we crossed the Rhine I knew we had another five to ten minutes to go and we would be safe.
            We passed several German fighters and they took no notice of us, and when we crossed the Rhine for
            some reason we lost another motor so from then on we came down fairly rapidly."
          </p>

          <p>
            Just then a tiny RAF Spitfire flew alongside and tried to lead them to an airfield, but they were losing
            height so rapidly they couldn't make it.
          </p>

          <p>
            "When I saw an opportunity to land the plane in a fairly largish area we just went straight in and
            landed."
          </p>

          <p>
            It was covered in snow which helped cushion the landing at nearly 200 kph. "We touched down and
            slid along on the underside of the aircraft for at least a kilometre, the ground was very wet and
            slippery, and I believe we did quite a lot of damage...we wiped out a concrete culvert, tore an engine
            off the aircraft, and destroyed a whole lot of fences."
          </p>

          <p>
            Within 10 minutes a RAF ambulance was on site. When the crew crawled out of the crashed plane
            Harry hardly recognised George Thompson who just said "Good landing, Skipper" before he
            collapsed.
          </p>

          <p>
            Harry had to give him and two of the gunners morphine injections for their pain. The injured men
            were taken to Eindhoven Hospital in the southern Netherlands. Ernie Potts died of his burn within 24
            hours. George Thompson died of pneumonia brought on by his extensive burns six weeks later.
            Thompson was awarded a Victoria Cross after his death, the only VC that 9th Squadron received. A
            street in the Dutch town of Heersch was named in his honour.
          </p>

          <p>
            Harry was awarded an immediate Distinguished Flying Cross for his skill, determination and courage
            in the most perilous circumstances for managing to land the damaged plane safely. He would have
            said simply that he was doing his job.
          </p>

          <p>
            Harry said little about that last brave and heroic flight. Fifty years later his navigator, Ron Goebbels
            and his wife Peggy, came to NZ to celebrate the fact that the crew felt they owed Harry for their
            lives. He said none of them thought they would get out of the plane alive.
          </p>

          <p>
            On that bombing raid six of the 66 Lancasters were shot down over the target. More than 110
            Lancasters were lost in WW2.
          </p>

          {/* Image 113: RAF sense of humour on bomb for Germany */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/113 RAF sense of humour on bomb for Germany.jpg"
              alt="RAF sense of humour on bomb for Germany"
              caption="RAF sense of humour on bomb for Germany"
              filenameHint="113 RAF sense of humour on bomb for Germany.jpg"
            />
          </div>
        </section>

        {/* THE TELEGRAM HARRY'S MOTHER DID NOT TRUST */}
        <section className="space-y-6 pt-6 border-t-2 border-[#d8ceb9]">
          <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#2c221e]">
            THE TELEGRAM HARRY'S MOTHER DID NOT TRUST
          </h2>

          <p>
            Harry's family back home got a telegram informing them Harry had been shot down and was last
            seen going down in flames. His mother, Annie, simply refused to accept that he was dead.
          </p>

          <p>
            Six weeks later they got another telegram, saying that he had been evacuated back to England and
            was recovering. He had frostbite and had a serious eye injury after hitting his head on the control
            column when they hit the culvert as they skidded along the ground after crash landing.
          </p>

          {/* Image 114: Harry Denton at Presentation Ceremony */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/114 Harry Denton at Presentation Ceremony.jpg"
              alt="Harry Denton at Presentation Ceremony"
              caption="Harry and Enid at the DFC Presentation Ceremony at Government House, from Governor General, Lord Bernard Freyburg"
              filenameHint="114 Harry Denton at Presentation Ceremony.jpg"
            />
          </div>

          {/* Image 115: Harry and Enid Denton */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/115 Harry and Enid Denton.jpg"
              alt="Harry and Enid Denton"
              caption="Harry and Enid Denton"
              filenameHint="115 Harry and Enid Denton.jpg"
            />
          </div>

          <p>
            <strong>AFTER THE WAR</strong> Harry returned to Oakdale and his relieved family.
          </p>

          <p>
            He entered ballots for a farm, as returning servicemen were able to do, and drew Block 2, Mount
            Brown Subdivision on Ram Paddock and Mound Roads inland from Amberley. He took possession of
            258 ha Dalrachie Farm in July 1949.
          </p>

          <p>
            He married Enid Cain from Dunsandel in April 1950 and the following year their Corriedale wool
            fleeces were sold in the boom wool auctions that resulted from the Korean War. That was a great
            start for the couple.
          </p>

          <p>
            Harry was keen on rifle shooting and won the NZ A Grade season aggregate title in 1957. He took up
            indoor and then lawn bowls, representing North Canterbury more than 50 times. He had learnt
            bridge in Canada and was a player and teacher for decades.
          </p>

          <p>
            Dalrachie Farm was sold in 1968. Harry and Enid retired to a new home on Douglas Road. Harry took
            a great interest in the stockmarket. They travelled, enjoyed their beach house at Amberley Beach,
            while Enid developed her painting and Harry was greenkeeper and bowls coach for many years.
          </p>

          <p>
            On the 50th anniversary of the crash landing in 1995 Harry and four of the crew went to Heesch to be
            present at the naming of a street in honour of Ft Sgt Thompson, and they visited the crash site. He
            said he had always wanted to go back and see the area again as when they crashed they were all in
            poor condition and their only concern was for the two men so badly burnt.
          </p>

          <p>
            When they were older Harry and Enid moved to a smaller home in Amberley. Harry died of cancer in
            2006. Enid died aged 101. They are buried at Balcairn Cemetery. They had a special relationship with
            Harry's niece Annette, and Derek Benfield, and their children Jordan and Charlotte." They were
            always kind, interested, supportive and tolerant," Annette said.
          </p>

          {/* Image 116: Harry and Enid headstones, Balcairn cemetery */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/116 Harry and Enid headstones, Balcairn cemetery.jpg"
              alt="Harry and Enid headstones, Balcairn cemetery"
              caption="Harry and Enid headstones, Balcairn cemetery"
              filenameHint="116 Harry and Enid headstones, Balcairn cemetery.jpg"
            />
          </div>

          <div className="p-4 rounded-xl bg-[#e8dec9]/60 border border-[#d8ceb9] text-xs text-[#4a3525] italic my-6">
            <p>
              Sources: Author Max Lambert's notes from an interview with Harry for his book "Night after Night"
              published in 2005, Harry's speech notes for an address to the Kowai Archives, Balcairn, 1995 &amp;
              Traces of War DFC Citation.
            </p>
          </div>
        </section>

        {/* POSTSCRIPT TO THE STORY OF FLYING OFFICER DENTON */}
        <section className="space-y-6 pt-6 border-t-2 border-[#d8ceb9]">
          <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#2c221e]">
            POSTSCRIPT TO THE STORY OF FLYING OFFICER DENTON
          </h2>

          <p>
            In the 1970's Willi Oermann contacted Harry through the English equivalent of the RSA. Willi was
            one of the German schoolboys who had been in the anti-aircraft crew responsible for shooting at
            and badly damaging the Lancaster Harry was piloting.
          </p>

          <p>
            Willi became a Professor at Bielefeld University. He had married Herta and they had four sons.
            Bielefeld is in the North Rhone - Westphalia area, between Cologne and Berlin. Bielefeld University
            is relatively new and has spectacular architecture in its site by the Teutoburg Forest.
          </p>

          <p>
            Harry and Willi became good friends and spent a summer together in NZ. Willi was traumatised by
            his war experience.
          </p>

          <p>
            After the 50th Anniversary commemorations in 1995, where Harry revisited the crash site, he went to
            Germany and stayed with Will and Herta. He brought home copies of the German newspapers from
            that time showing the devastation of many parts of German from WW2.
          </p>

          <p>
            Three of Will and Herta's sons came to NZ to see Harry and Enid at Amberley. The friendship
            continued until Enid's death in 2019. Willi's daughter in law Andrea Oermann attended Enid's
            funeral.
          </p>

          {/* Image 117: Press clipping about Harry and his crew */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/117 Press clipping about Harry and his crew.jpg"
              alt="Press clipping about Harry and his crew"
              caption="L-R: Ron Goebel, Taffy Price, Harry Denton, Ted Kneebone, Wilf Hartshorn (Anniversary documents courtesy Annette Benfield)."
              filenameHint="117 Press clipping about Harry and his crew.jpg"
            />
          </div>

          <p className="font-serif italic text-base text-[#4a3525]">
            Navigator Ted Kneebone wrote this poem about their crewmate, George Thompson.
          </p>

          {/* Poem: George */}
          <div className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] shadow-xs space-y-4">
            <h3 className="font-serif font-bold text-lg text-[#2c221e] uppercase tracking-wider">
              GEORGE
            </h3>
            <div className="font-serif italic text-base sm:text-lg text-[#2c221e] space-y-2 leading-relaxed">
              <p>"Buried in a foreign field, A hero lies in state</p>
              <p>His body from the world concelaled, A stone above to mark his fate.</p>
              <p>The call to freedom in his ears, He left the ones who loved him most</p>
              <p>And midst the roar of battle fear, He died and joined a gallant host.</p>
              <p>Read the neat row of etching, On the stone placed over his head</p>
              <p>It tells you nothing of suffering, Only records he is dead.</p>
              <p>When the war is o'er and battle done, And all cry out "Thank God we've won"</p>
              <p>Let's not forget how he helped you and me, in becoming a hero, posthumously."</p>
            </div>
          </div>
        </section>

        {/* MEMORIAL TO THE NZ AIRMEN WHO SERVED IN WW2 BOMBER COMMAND */}
        <section className="space-y-6 pt-6 border-t-2 border-[#d8ceb9]">
          <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#2c221e]">
            MEMORIAL TO THE NZ AIRMEN WHO SERVED IN WW2 BOMBER COMMAND
          </h2>

          {/* Image 118: Lancasteer at MOTAT in Auckland */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/118 Lancasteer at MOTAT in Auckland.jpg"
              alt="Lancaster at MOTAT in Auckland"
              caption="MOTAT 2 at Western Springs, Auckland has this four engine Avro Lancaster Bomber Mark 7 as a star attraction. It was gifted to NZ by the French government as a memorial and tribute to NZ airmen's service under Bomber Command."
              filenameHint="118 Lancasteer at MOTAT in Auckland.jpg"
            />
          </div>

          {/* Image 119: Inside lancaster bomber cockpit */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/119 Inside lancaster bomber cockpit.jpg"
              alt="Inside lancaster bomber cockpit"
              caption="Inside the cockpit of the Lancaster Bomber"
              filenameHint="119 Inside lancaster bomber cockpit.jpg"
            />
          </div>

          {/* Image 120: Letter to The Press from Harry_s crew */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/120 Letter to The Press from Harry_s crew.jpg"
              alt="Letter to The Press from Harry_s crew"
              caption="Letter to The Press from Harry's crew"
              filenameHint="120 Letter to The Press from Harry_s crew.jpg"
            />
          </div>
        </section>

        {/* LETTERS FROM HARRY TO HIS BROTHER WARREN DURING WW2 */}
        <section className="space-y-6 pt-6 border-t-2 border-[#d8ceb9]">
          <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#2c221e]">
            LETTERS FROM HARRY TO HIS BROTHER WARREN DURING WW2
          </h2>

          {/* Letter 1 */}
          <div className="space-y-4 bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] shadow-xs">
            <div className="font-serif text-base sm:text-lg italic text-[#2c221e] space-y-4 leading-relaxed">
              <p>
                Things are moving here too. I had the freight commando's test last week and didn't do too good but
                good enough to get past I think. I have been having fun lately doing loops and slow rolls. Loops are
                very easy, but the roll is not quite so easy. I am on night flying tonight at 9.15 and have to be on
                lectures as usual in the morning. We only have 3 hours if we go solo and 4 if we don't. This will be
                my second hour. I didn't do too good the first time with a stiff cross drift.
              </p>
              <p>
                I had my overseas medical this afternoon and did a lot better than at Rotorua. We finish up here at
                the end of next week so maybe on final leave by the end of the month. I am a sitter for multi-
                engined planes, so I have gone and got top marks again for Nov. at the mid-term exams, and my Nov
                in the air is just as good.
              </p>
              <p>
                Last week we went to Timaru and back on a triangular course. My Nov. was good but sold out on the
                second leg. It was the rotten bacon and eggs we had for breakfast. As soon as I sold out, I was all
                night and flew home. It was pretty rough.
              </p>
              <p>
                We are going on a solo cross country this week to Harewood and home via Southbridge. Do you
                think I can find it? The track passes over Ellesmere and follows the rail in and then passes over Bruce
                Ford's house. Bruce Ford has brought Dave Moorhead's farm and Ray Ford is going into Bruce's
                house. We are not allowed to muck about and if we do it will be too big a risk. I will have one flip
                over home before I finish anyway.
              </p>
              <p>
                I was home the weekend again and the wheat will all be stacked by now, 4 Army jokers having a
                good time if I know anything. Hampton is having trouble getting out. If you have any say I think you
                would do well to put in for Ashburton as it seems to be the best, no bullshit, and leave whenever
                possible. I have been home every weekend so far and once on Friday and once on Thursday so that
                is good.
              </p>
              <p>
                Harewood only get one weekend a month and a lot of drill and bullshit of all sorts. Of course, this
                place is a bit rough, but we mostly sleep anywhere. Time is up so must go.
              </p>
              <p className="not-italic pt-2">
                Harry
              </p>
            </div>
          </div>

          {/* Letter 2 */}
          <div className="space-y-4 bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] shadow-xs">
            <div className="text-xs font-serif font-bold text-[#7a5b3a] uppercase tracking-wider space-y-0.5">
              <p>A.D.U.</p>
              <p>R.N.Z.A.F. Station</p>
              <p>Woodbourne</p>
              <p>Blenheim</p>
              <p>Wed.</p>
            </div>
            <div className="font-serif text-base sm:text-lg italic text-[#2c221e] space-y-4 leading-relaxed">
              <p>Dear Warren</p>
              <p>
                A good opportunity to answer your letter now while it is quiet. You would find the money looks good
                on payday. If it is payday on Friday which it was last week, the boys say that the eagle shits on
                Friday. Airforce slang is usually expressive but not always printable.
              </p>
              <p>
                Our guard duties have settled down now. I was threatened with court-martial for refusing the
                special police arm band, but we all stood together and now we wear a plain white band which
                means nothing really. I was on moving patrol on Sunday night from 8-10 and 2-4 in the morning.
                Hopped on the bike when I came off and rode home and went to bed.
              </p>
              <p>
                We are excused duties till midday and have lectures in the afternoon. I was on mess Tuesday and am
                on moving patrol Thursday and guard on the gate to the dormitories on Sat night, so will be pretty
                busy. We have a morse test at 12, words and a P.E. test tomorrow, but it won't be much. We have
                the big test on the first 10 assignments next week as the other camp take over all the guard and we
                take on the Tower lookout which is easy as we will be able to swat. We have the aircraft recognition
                test on Friday that the A.P's had for their wings exam.
              </p>
              <p>
                We finished ass.11 today and it is the start of the algebra but just like the old ass. No 1, in fact the
                whole section from 10 to 15 is very good just repetition. I have got the bike going well and it is very
                handy to run over to the house and saves ½ hour each way, especially as we can ride through the
                river now.
              </p>
              <p>
                Had a game of tennis on Sunday and felt pretty sore next day. With practice I should be alright. The
                restring cost 30 shillings and with a press and pair of balls cost £1/9/6. The outfit seems to be alright
                too. George Lean the ½ Chinese boy won the table tennis championships last night. He played for
                South Australia at one time and showed me a bit about tennis too.
              </p>
              <p>
                Happy and his guitar must be popular. We are putting on a dance on the station on Sat in the
                Y.M.C.A. The band here is piano, 2 piano-acc and a bongo-mandolin and the sax player is looking for
                one as he sold his before coming in. I may get leave in about six week's but it is not certain and there
                is a possibility of a week's leave before going to Rotorua.
              </p>
              <p>
                We have been having wonderful weather here this week so I suppose you will get it too. You are
                going to have to a warm summer which should suit you down to the ground. Have you got your
                shoulder budgies yet? I don't suppose you will get blue caps. You seem to have struck it lucky with
                the engine. Can you get your weight down to 12-7? I don't think you have a hope but who cares.
              </p>
              <p>
                We will be here about 3 months more I think and only a bit over 2 so not half-way yet. The latest
                idea here is to make a garden in the orchard of old apple trees and it is all old roots and clay and
                cocksfoot. All the farmers here are laughing but suppose it will go on. Well, there isn't much to say
                so I had better get on with the revision.
              </p>
              <p className="not-italic pt-2">
                Cheerio<br />
                Harry
              </p>
            </div>
          </div>

          {/* Letter 3 */}
          <div className="space-y-4 bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] shadow-xs">
            <div className="text-xs font-serif font-bold text-[#7a5b3a] uppercase tracking-wider space-y-0.5">
              <p>A.D.U.</p>
              <p>R.N.Z.A.F. Station</p>
              <p>Woodbourne</p>
              <p>Blenheim</p>
              <p>Thursday (green letter)</p>
            </div>
            <div className="font-serif text-base sm:text-lg italic text-[#2c221e] space-y-4 leading-relaxed">
              <p>Dear Warren</p>
              <p>
                It is 12.30 and I am on middle piquet and not much chance of writing any other time. I think that
                Tauranga will be just about the best place in N.Z. for you as it is a great health resort, although your
                present camp must be all right. You should be able to get home at the New Year for a few day's I
                think. You won't know what to do with the pay in a month or so. You get £5-8-3 twice a month so
                you should get some pretty soon.
              </p>
              <p>
                We have had to take over the guard on the main gate and the patrol round the hangers starting
                tonight. I am on patrol with fixed bayonet and 5 rounds from 6 tonight till 6 Friday morning. 2 hours
                on and 4 off and I want to go to town Friday night for the bike and tennis racquet. It will cost 30/- for
                a good restring as about eight strings were done. I brought the bike up and as it was raining and the
                river between us and the drone in flood we had to take a taxi round the back way.
              </p>
              <p>
                That is 10 days ago and no leave to get it yet. It will be very handy travelling back and forwards to
                Station on guard. We are fighting it all the way as it is not our job at all, and we are making it so
                tough for everyone that they will be glad to get rid of us. We are stopping everyone regardless and
                demanding passes. Anyone without, we run straight in, and the C.O. was the first. This was in the
                daytime to. We can't do guard all night and make a job of classes in the daytime.
              </p>
              <p>
                The head of the Education part of the business came round and he said that the first draft from here
                go out in Oct. not Sept. and we'll all be gone before Xmas. You won't be called before March as far
                as I can make out as the waiting list is pretty big. There are hundreds working as A.C.Hs and they go
                first. I don't think you will have any trouble passing the exam now and they will give you a pretty
                good spin I think.
              </p>
              <p>
                I can do 10 words and we will soon be up to 15 the instructor says. You needn't worry about it either
                we will have a test again tomorrow and finish ass.8 after four weeks, ass 10-15 are being printed so
                we are not hurrying. Stick to your old book and don't let them see it. There are no trigs or logs, but
                the aim now is rapid mental work, and the time regularly beats me. My marks so far are 90, 77, 66,
                86 and better than most. You must look out for Tommy Howe, he missed the exam and is with you
                somewhere. His brother is here with me tonight and is a very quiet chap. I get on well with him too,
                his name is Roy.
              </p>
              <p>
                I had a good trip home and didn't get sick or anything silly, in fact travelled pretty well. Isabel and
                Mrs Bryce met me in town. I had to wait an hour because the old girl was too hungry to come, and
                what could Isabel do. She tried to dump her. We got home about 1 o'clock and then went to dance
                at Southbridge.
              </p>
              <p>
                Ballagh is in camp. Everyone stared at me, and it was funny to hear what was said. Some of the boys,
                back from Sutra were there. Angus Carr played the tenor banjo as Leo is in camp again, but it was
                hard to hear him.
              </p>
              <p>
                Bruce McLeod's brother is here too. Have you heard that Bruce is suspected of having hydatids and
                has stopped flying. Joyce is not going out anymore now. There has been a lot of flu here lately and
                so far, I have missed.
              </p>
              <p>
                I haven't had a game of football yet and we haven't time to play now. Don Yeates is here and has his
                bulldog pup, six months old and a spoilt pedigree too. We have the best bunch of chaps here that I
                have ever been mixed up with. Many of them provincial champions, and our P.T. instructor was
                world champ skulls at one time, but we only see him sometimes. There are a few who shouldn't be
                here at all. I must be wandering a bit.
              </p>
              <p>
                It was raining when I left home and I just went straight to bed at the Union Jack Club, and not a bad
                place either. It only cost me £2-10 travelling expenses. We did no lectures last week as the
                footbridge washed away and no way of travelling. It rained all week, and I was on mess orderly too.
                The cook went AW last Sat. so we did without till Tuesday. The boys took turns at it and made a
                good job too. The mess orderlies worked just the same and there was no pinching going on. You will
                be hungry for a week or two, I suppose. We are only eating about half what we did the first 3 weeks
                and I don't eat much at all now. Change of climate mostly.
              </p>
              <p>
                You must have had a good time at Rongatai. They treat you pretty well on the Stations like that. The
                paper question is acute here too. This is the last page of the old pad. I will send you a packet of razor
                blades I picked up in town. They can't be bought here at all.
              </p>
              <p>
                The officer in charge of this and the No.2 camp arrived today but I have not seen him yet. Rowed in
                the Olympic Games I think. Some of our fun will be over now unless he is a pretty good sort. I am
                pretty sure to get to Rotorua before xmas so might be able to meet you on leave some time. You are
                going to be hard put to find something to do or somewhere to go. I think Tauranga is not so very far
                from Auckland so that is something, but not much if you don't know anyone.
              </p>
              <p>
                I saw your letter home. You mention an old CYC man. I am interested as there are about 6 here. Your
                number is something like mine N.Z.426,036 not the old one. I don't think I will go home next leave as
                it is too much of a rush, but most of the boys here are from Southland and they go home but the
                connections are just right, and they change trains at Add.
              </p>
              <p>
                We are getting a reputation as the toughest customers ever to come on to this Station. The trouble
                is that they try to put too much on to us and when we move, we all go together. It is nearly 2.30 so I
                had better finish and get off to bed again. There won't be much rest tomorrow night with 2 two hour
                shifts on patrol. Only hope I catch the S.W.O. as pretty hard chap and not at all well liked, but we get
                on all right.
              </p>
              <p>
                There has been a 100% improvement in behaviour since we came and set the pace. The Wasps are
                getting a better hearing too as we shook things up there too.
              </p>
              <p className="not-italic pt-2">
                Cheerio for now<br />
                Harry
              </p>
            </div>
          </div>

          {/* Letter 4 */}
          <div className="space-y-4 bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] shadow-xs">
            <div className="text-xs font-serif font-bold text-[#7a5b3a] uppercase tracking-wider space-y-0.5">
              <p>A.D.U.</p>
              <p>R.N.Z.A.F. Station</p>
              <p>Woodbourne</p>
              <p>Blenheim</p>
              <p>Thursday</p>
            </div>
            <div className="font-serif text-base sm:text-lg italic text-[#2c221e] space-y-4 leading-relaxed">
              <p>Dear Warren</p>
              <p>
                It is raining this morning and I am feeling pretty miserable. We all got two injections yesterday
                afternoon, the first two, and my left arm is very stiff, in fact one of the worst in camp. We get
                another one next week and had the vaccination last Sunday, but it hasn't started to work yet and
                doesn't show any sign of working yet.
              </p>
              <p>
                We had the exam on Monday and Tuesday, and they held the same exam at several other stations. I
                got top marks out of seventy odd who sat here with 99%. There was one 98, one 97 and two 96. The
                average for the flight was 90.2%, but only about 13 out of the other two flights passed 90 and
                nobody failed. It was too easy after the weekly tests in Mental, air navigational with any amount of
                time. General mathematics and science took longer, and several didn't finish. I finished but only had
                time to check half of it. There were no slide rules used. The mistake I made was silly and the only
                one in camp to make it, I think. The question was - How do parallels of latitude appear on a
                topographical map? I said straight lines whereas they are concave.
              </p>
              <p>
                I wonder if there were any observers sitting at Tauranga. Some sat here who had only been in about
                3 weeks, but they had all got metric, so it was not so hard. The teacher F/O McKenzie is going to find
                out if anyone at the other stations beat me. They may try to make me an observing now, but I don't
                like the idea at all. The observers are on leave now and they will only spend one more night here
                before going on to Rotorua. Robbie is going so I will have to get a new hut mate. I am shifting in with
                Vern Armstrong from Akaroa, a quiet farmer. I may go on leave in a week's time.
              </p>
              <p>
                Tuesday night after the exam the boys went wild, and I think there were 36 gals of beer consumed in
                a very short time. It was about 2am before the camp settled down and I could go to sleep. My hut is
                right on the track from the mess and they apple-pied my bed and tried to pull me out several times
                till I got the rifle and anyone who came through the door got a solid prod in the stomach.
              </p>
              <p>
                Robbie turned up at midnight helpless, so I got him into bed, and he slept through it all. They were
                pelting the huts with boulders and pissing against the walls, and in the next hut drank a gallon near
                1am and then some of them got sick.
              </p>
              <p>
                The observers going on leave were pulled out at 5 to get away and the camp was filthy. I don't think
                there will ever be another wild night in camp again as the officer was exceeding his authority in
                allowing it all, and it went further than he said.
              </p>
              <p>
                I have been told by one of the observers that the list for Rotorua next month is in the orderly room
                and I am supposed to be in it, but I will believe it when I see it and it doesn't look like coming out till
                tonight at least. We parade shortly so had better close now. Things are very slow now the exam is
                over, and we didn't do much but swat the last week.
              </p>
              <p>
                Once you pass the 10th assignment the most is done and after that it is much like the others. I don't
                think the next test will be quite so easy because the Education Officers here will set part of it. It has
                stopped raining so we will have to go for a route march. We have just had a gas lecture and it is
                quite interesting.
              </p>
              <p className="not-italic pt-2">
                Must go now so cheerio<br />
                Harry
              </p>
            </div>
          </div>

          {/* Image 121: Harry_s letter to brother Warren */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/121 Harry_s letter to brother Warren.jpg"
              alt="Harry's letter to brother Warren"
              caption="Harry's letter to brother Warren"
              filenameHint="121 Harry_s letter to brother Warren.jpg"
            />
          </div>
        </section>

        {/* Harry's notes his trip to Europe in 1995 */}
        <section className="space-y-6 pt-6 border-t-2 border-[#d8ceb9]">
          <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#2c221e]">
            Harry's notes his trip to Europe in 1995, on the 50th anniversary of the end of WW2
          </h2>

          <p>
            The 50th anniversary of the end of the European was celebrated recently and I was in three different
            countries during the first week of May. It was quite interesting to see a difference in attitudes. At
            our own Anzac parades we remember those who died in foreign lands fighting to preserve our
            freedom and way of life which would have been quite different had the allies not prevailed. The
            accent here was on the returned services personnel.
          </p>

          <p>
            In England they celebrated a victory after a long and butter struggle directly involving the civilian
            population. At one time they faced the real prospect if invasion and defeat. There was more a sense
            of elation. I was in London on VJ Day 1945, the true end of the war, and will never forget the sense
            of relief that it was all over. At that time I was preparing to join the forces against Japan with a
            Mosquito Bomber Squadron.
          </p>

          <p>
            On 8 May this year I was in Germany. No celebrations, but newspapers published supplements
            depicting the results of war as they affected Germany, remembering the destruction and misery of
            the people and remembering some oof the terrible things the Germans had been involved in such as
            the Holocaust, the concentration camps and the perversion of truth. The attitude was that it must
            never happen again and with the integration of Europe I am sure it never will.
          </p>

          <p>
            It was quite different in Holland where the people suffered 4.5 years of occupation and repression.
            The Dutch resisted wherever they could and never gave up. This brought cruel reprisals and during
            the final weeks of fighting they were literally starving to death, with quite large areas still in German
            hands, although the Allied Forces were racing towards Berlin and Germany was on the point of
            collapse. The situation was so desperate that RAF and American bombers dropped 10,000 tons of
            food on low level flights to civilian populations even in areas still held by German forces and prison
            camps. The Dutch remember.
          </p>

          <p>
            On 1 January 1945 I was the pilot of a Lancaster bomber, almost destroyed while bombing the
            Dortmund Emms canal in a daylight raid. About an hour later we crash landed in a field on the
            outskirts of the small Dutch town of Heesch, which had already been liberated. We were picked up
            by an RAF Ambulance and taken to hospital at Endhoven, so had little contact with local people.
            Our W/O was awarded the Victoria Cross for rescuing both the MV and rear gunners from their
            burning turrets, but later died from burns he received in doing so.
          </p>

          <p>
            About a year ago the town of Heesch, now with a population of 10,000 and the centre of a county
            was planning a new development which involved a new street, no more than 100m long, to connect
            the main street with another parallel street for better access. New streets had been named after J F
            Kennedy. Grenadierstraat, commemorating the British Grenadiers who were involved in the
            liberation of Heesch. The story of the Lancaster crash at Heesch had become known, so the town
            council decided to name the street for George Thompson. They also knew that the pilot was a NZ'er.
          </p>

          <p>
            Last December a brief description of that flight the invitation to the opening ceremony, so I decided
            to join them, and be with my crewmates again, for probably the last time. It appeared in the NZ RSA
            Review, asking me to contact a Dutch address, which I did. In January I had a letter about the street
            naming ceremony planned for the first week of May and was able to supply the addresses of the
            other four survivors, all British. By this time the Dutch had made contact with George Thompson's
            two sisters. They accepted the invitation to the opening ceremony, so I decided to join them and be
            with my crewmates again, for probably the last time. Facing almost certain death together creates a
            bond that lasts a lifetime.
          </p>

          <p>
            The locals arranged a programme for our entertainment, and we were made so very welcome
            wherever we went. We arrived on Tuesday evening 2 May and visited the crash site where we met
            about 50 locals and 2 television crews plus radio and press reporters. Can you imagine the results,
            five people trying to interview us, the organisers and locals. We saw the results in the local papers
            but missed the TV programmes.
          </p>

          <p>
            We were billeted with English-speaking families who made us most welcome. Next day there was an
            official welcome at the town hall. The Burgomaster or Mayor was a charming lady with a good
            command of English. Also present were a colour party from 9 Squadron, now stationed at Brusggen
            in Germany, with the Squadron Colours paraded for the first time since the Gulf War. Most of the
            Squadron were deployed in Saudi Arabia watching the Iraqis, but we saw some of the Squadron
            treasures, trophies and paintings.
          </p>

          <p>
            The German gunners who shot us down included 15 and 16 year old schoolboys known as flak
            helpers. Two of them had accepted an invitation to attend, and they were well received. Europe is
            now so integrated that there is little animosity noticeable today. I had met them previously and
            afterwards returned to Germany with Willi Oermann who visited Amberley five years ye
            ago.
          </p>

          <p>
            The street naming ceremony at noon was performed by the Burgomaster assisted by Joan
            Thompson, now a retired hospital matron. It reads Sgt. Thompsonstraat and underneath "Flight
            Sergeant George Thompson V.C." The Dutch flag covered and name and at the other end of the
            street the name was covered by the European Union flag, a circle of yellow stars on a blue
            background. I had the privilege of unveiling the second sign with about 100 onlookers.
          </p>

          <p>
            This was followed by an official luncheon at the town hall, to which the Thompson sisters were
            transported in an open horse drawn carriage used when the Dutch Queen visits.
          </p>

          <p>
            Later that afternoon the crew members returned to the crash site for a better look around. This part
            of Holland is flat, with many quite small drains, closely settled with lots of trees. Our landing site was
            little changed, bigger than I had thought and the only possible site for miles around. From
            touchdown we skidded wheels for about a kilometre, took out several wire fences, lost a motor, torn
            off when we hit a culvert and a dyke and came to rest a few metres from a road and trees. Very
            lucky anyone survived.
          </p>

          <p>
            That evening we were entertained by a brass band and choir, both excellent, and three short historic
            films.
          </p>

          <p>
            Next day we were able to visit the graves at Eidenhoveen and Brussels of our crewmates who failed
            to survive. This took most of the day and involved a lot of travelling.
          </p>

          <p>
            In the evening I attended a service in memory of the 20 odd solders killed in the liberation of Heesch
            and buried in a section of the local cemetery. Children placed flowers on each grave. This is done
            very year.
          </p>

          <p>
            I had six days in Germany and visited the Monhe Dam, target of the Dambusters Squadron. I had
            eight days in Scotland and in England visited Chartwell, Winston Churchill's home since 1922,
            Clandon Park, home of the Earl of Onslow, Governor General of NZ 1988-92. In the grounds was the
            Maori meeting house that survived the Tarawera eruption at Te Waieroa, well preserved. I also
            visited Portsmouth and went over Victory, Nelson's Flagship, the Warrior, Britain's q1860 battleship,
            the first with a steel covered hull steam engine and sails and the Mary Rose, Henry the Eight's
            flagship.
          </p>

          <p>
            The journey home took 30 hours with only two hours in the transit lounge at Los Angeles.
          </p>

          <p>
            I am glad I made the effort to go back and see people and places that have had such an influence on
            my life.
          </p>
        </section>

        {/* HARRY'S CREW */}
        <section className="space-y-6 pt-6 border-t-2 border-[#d8ceb9]">
          <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#2c221e]">
            HARRY'S CREW¹
          </h2>

          <p>
            "I'm browned off," wrote AC George Thompson, 22-year-old son of a Kinross ploughman, seizing the
            chance of a short break in his ground wireless duties to write a letter home. I was 1 January 1943
            and he thought he had never spent a duller New Year's Day. "It's not that I want to do any of that
            heroic stuff but this job isn't very exciting."
          </p>

          <p>
            Twelve months overseas service in Iraq, Persia and the Persian Gulf had not satisfied George
            Thompson's desire for adventure, and soon after waiting this letter he applied for a transfer to flying
            duties. Two years later, on another New Year's Day, "that heroic stuff" was to win him the Victoria
            Cross.
          </p>

          <p>
            Thompson left school at 14 to become a grocer's assistant and he later became a certified grocer. He
            was tall, big boned and immensely strong. He had a brusque downrightness of speech with a strong
            Scots accent. His rugged exterior was softened by the gentle manner of the typical highlander -
            quiet, obliging, conscientious. He saw nothing menial in the service of his fellow men. As a boy he
            loved tinkering with wireless sets and many a time on his grocery van rounds he traced and repaired
            some minor fault for a lonely couple whose radio was their only contact with the outside world.
          </p>

          <p>
            His application for aircrew training was successful and in the late autumn of 1944 he reported for
            duty with his crew at No 9 Squadron at Bardney.
          </p>

          <p>
            His pilot was Harry Denton (25) a farmer from NZ - quiet and shy, studious and teetotal, not a good
            mixer socially but keen on a good crew spirit. On their arrival at the operational training unit Denton,
            looking around for likely crew, had picked out Ron Goebel, a tall, fair, pale young man who had
            trained as a navigator and then been transferred to bomb training because of a shortage Denton
            found Goebel keen and surprisingly mature for his age, and the two men agreed to join forces. Like
            Denton Goebel drank little and did not smoke and took life fairly seriously without being devoid of a
            sense of humour.
          </p>

          <p>
            Next Denton was introduced to a navigator named Ted Kneebone, an NCO from Manchester, slightly
            built and alert, neat and quick in his movements at his work.. It was Kneebone who picked out
            George Thompson, impressed at once by the steady brown eyes and open face and pleasant Scots
            burr. The two men became firm friends and went everywhere together. Kneebone found Thompson,
            although outwardly the strong silent type, thoroughly articulate on subjects which interested him
            and fanatically proud of his Scots ancestry, which he was always prepared to defend in argument, if
            necessary with his fists. He was no prude, and liked his pint of beer but he had a deep religious sense
            and the two men frequently went to Evensong together on a Sunday, calling in afterwards at a local
            pub to listen to the talk of the country folk.
          </p>

          <div className="p-4 rounded-xl bg-[#e8dec9]/60 border border-[#d8ceb9] text-xs text-[#4a3525] italic my-6">
            <p>1 From "Strike Hard, Strike Sure" by Ralf Barker</p>
          </div>

          <p>
            It was Kneebone too who picked the two Welsh gunners, Hydn Price - short and thick set, with a
            slow friendly smile - and Ernie Potts, oldest man in the crew, married and exempt from was service
            because of his work as an agricultural engineer, until he volunteered. His wife had recently had a
            baby daughter, Christine.
          </p>

          <p>
            Both Price and Potts, true to Welsh tradition, loved singing and the intercom was often taken up
            with Welsh melodies.
          </p>

          <p>
            New Year's Ever 1945 and an All Ranks Dance was underway. Then the tannoy blared "Attention
            Pease. Attention Pease. The following pilots will report to the Operations Room"
          </p>

          <p>
            Ten Lancaster pilots were called to duty, including Frederick Harry Denton, and 60 crew members.
            They were woken at 5 am, on a very cold morning which turned into a hard frost. The Lancasters
            were white with hoar frost, and before they turned onto the runway each plane was sprayed with
            glycol.
          </p>

          <p>
            Harry's Lancaster was the first to take to the air. Whether it was ice still on the wings, the lack of
            wind or carburettor icing is uncertain but the second aircraft crashed at the end of the runway and
            burst into flames, but the crew escaped.
          </p>
        </section>

        {/* DAVID STEWART GIBB */}
        <section className="space-y-6 pt-6 border-t-2 border-[#d8ceb9]">
          <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#2c221e]">
            HARRY'S SECOND COUSIN DAVID STEWART GIBB ALSO RECEIVED A DFC FOR HIS RAF SERVICE IN
            THE KOREAN WAR (1950-53)
          </h2>

          {/* Image 122: Distinguished Flying Cross */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/122 Distinguished Flying Cross.jpg"
              alt="Distinguished Flying Cross"
              caption="Distinguished Flying Cross"
              filenameHint="122 Distinguished Flying Cross.jpg"
            />
          </div>

          <p className="font-serif italic text-base text-[#4a3525]">
            Fragments from a letter his father George Gibb wrote to his cousin Ted Roberts during WW2.
          </p>

          {/* George Gibb Letter */}
          <div className="space-y-4 bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] shadow-xs">
            <div className="text-xs font-serif font-bold text-[#7a5b3a] uppercase tracking-wider space-y-0.5">
              <p>Craig Knock</p>
              <p>April 29. 41</p>
            </div>
            <div className="font-serif text-base sm:text-lg italic text-[#2c221e] space-y-4 leading-relaxed">
              <p>Dear Ted</p>
              <p>
                I think I owe you a letter and must attend to writing a reply, it is easy to start but my mind is so slow
                it takes quite a while to collect enough to fill a couple of pages, although there is enough going on in
                the world to think about.
              </p>

              {/* Image 122A: part of letter */}
              <div className="my-6 not-italic">
                <ArchivalImageCard
                  src="/images/122A part of letter.jpg"
                  alt="part of letter"
                  caption="Extract from George Gibb's letter"
                  filenameHint="122A part of letter.jpg"
                />
              </div>

              <p>
                ...we are keeping a few less sheep this year. We had letters from David² last week. He's in Scotland
                somewhere in good quarters and good health, some of his mates are still with him- he says nothing
                about flying machines. These big new bombs must be powerful means of distruction.
              </p>
              <p>
                The last time I was in Christchurch I called to see your mother³ and Maggie, your mother was as
                cheerful as she was half a century ago, her hearing is a sore trial, we went back into some old
                history, her mind is wonderful, there is often mentions in our paper about aids to hearing - has she
                tried any of them? Alice intends to make some enquiry, her hearing has failed a lot.
              </p>
              <p>
                Baby brother Tom is in fair good tune, but would be a crew man if there was a more enlightened
                government managing our country. Our old member has decided to retire. I have always had a lot of
                time for him, Mr Holland is the fighting sort of man we want now.
              </p>
              <p>
                Now I have got my two pages of odds and ends, so will say goodnight, and hope that you are in good
                health also your wife and girls, also Edward.
              </p>
              <p className="not-italic pt-2">
                With good best wishes for the future, yours sincerely<br />
                George and Alice
              </p>
            </div>
          </div>

          {/* Image 123: David Gibb headstone */}
          <div className="my-8">
            <ArchivalImageCard
              src="/images/123 David Gibb headstone.jpg"
              alt="David Gibb headstone"
              caption="Waikari Cemetery - amongst many of the family headstones"
              filenameHint="123 David Gibb headstone.jpg"
            />
          </div>

          {/* Footnotes 2 and 3 */}
          <div className="p-4 rounded-xl bg-[#e8dec9]/60 border border-[#d8ceb9] text-xs text-[#4a3525] italic my-6 space-y-1">
            <p>2 David Stewart Gibb was a pilot, got the DFC</p>
            <p>3 Sarah Roberts was living in Champion St, Christchurch with her daughter Maggie</p>
          </div>
        </section>

        {/* Navigation Footer to Previous/Next Chapter */}
        <div className="pt-8 border-t-2 border-[#d8ceb9] flex flex-col sm:flex-row items-center justify-between gap-4">
          <button
            onClick={() => onSelectPart && onSelectPart('part7')}
            className="w-full sm:w-auto px-5 py-3 rounded-2xl bg-[#e8dec9] hover:bg-[#d8ceb9] text-[#2c221e] font-serif font-bold text-sm transition flex items-center justify-center gap-2 cursor-pointer shadow-xs"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Part 7: Tommy Denton</span>
          </button>

          <button
            onClick={() => onSelectPart && onSelectPart('part9')}
            className="w-full sm:w-auto px-5 py-3 rounded-2xl bg-[#7a5b3a] hover:bg-[#5c4328] text-white font-serif font-bold text-sm transition flex items-center justify-center gap-2 cursor-pointer shadow-xs"
          >
            <span>Part 9: Isabel &amp; Warren Denton</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>

      </article>
    </div>
  );
};
